import {
  ref,
  onMounted,
  defineComponent,
  computed,
} from "@vue/composition-api";
import { JSConn } from "@/utilities/js-conn";
import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation.vue";
import { WorkOrderTask } from "@/store/modules/workOrderTasks";

import { itemRouteBuilder, NamedRts } from "@/router";
import { genericSort, sortIcon } from "@/components/helpers";

import StatusIndicator from "@/components/StatusIndicator/StatusIndicator.vue";
import ListPageHeader from "@/components/ListPageHeader/ListPageHeader.vue";

import { debounce } from "@/utilities/debounce";

import {
  arrowUpHandler,
  arrowDownHandler,
  filterOnInputHandler,
  filterItems,
} from "@/utilities/pickListHelpers";

export default defineComponent({
  name: "workQueue",
  props: {
    useMode: {
      type: String,
      required: false,
    },
  },
  components: {
    LoadingAnimation,
    StatusIndicator,
    ListPageHeader,
  },

  setup(props, ctx) {
    const $store = ctx.root.$store;

    const dataLoaded = ref(false);
    const loadingMessage = "Loading the tasks...";

    const firstInput = ref<null | HTMLInputElement>(null);

    const filterableKeys: Array<keyof WorkOrderTask> = [
      "name",
      "client",
      "client_field",
      "client_field_description",
      "commodity",
      "start_date",
      "completed_date",
    ];

    const filter = ref("");

    const clearFilter = () => {
      filterOnInputHandler(
        apiWorkOrderTasks,
        workOrderTasks,

        "",
        filter,
        debouncedFilteredWorkOrderTasks,

        sortKey,
        sortAsc,
        updateSort,

        selectedIndex,
      );
    };

    const filterWorkOrderTasks = (filter: string, wot: WorkOrderTask[]) => {
      return filterItems(filter, filterableKeys, wot);
    };

    const debouncedFilteredWorkOrderTasks = debounce(filterWorkOrderTasks, 500);

    const sortKey = ref<keyof WorkOrderTask>("client");

    const sortAsc = ref(true);

    const apiWorkOrderTasks = computed(() => {
      return $store.getters.workOrderTasks as WorkOrderTask[];
    });

    const workOrderTasks = ref<WorkOrderTask[]>([]);

    const localSortIcon = (lSortKey: keyof WorkOrderTask) => {
      return sortIcon(sortAsc, sortKey, lSortKey);
    };

    const updateSort = (lSortKey: keyof WorkOrderTask) => {
      workOrderTasks.value = genericSort(
        [...workOrderTasks.value],
        sortAsc,
        sortKey,
        lSortKey,
      );
    };

    const showRouteBuilder = (id: string) => {
      return itemRouteBuilder(NamedRts.workOrderTasks, id);
    };

    onMounted(() => {
      const jsConn = new JSConn();

      jsConn.formParams = {
        status: "queued",
      };

      Promise.all([$store.dispatch("needWorkOrderTasks", jsConn)])
        .then(() => {
          workOrderTasks.value = [...apiWorkOrderTasks.value];
          // flip the sort, otherwise on load it would be off
          sortAsc.value = !sortAsc.value;
          updateSort(sortKey.value);
        })
        .finally(() => {
          dataLoaded.value = true;
          ctx.root.$nextTick(() => {
            if (firstInput.value) {
              firstInput.value.focus();
            }
          });
        });
    });

    // this is for when the tasks are used as a picker
    // they make the arrow keys work correctly
    const selectedIndex = ref(-1);

    const onArrowDown = (evt: any) => {
      evt.preventDefault();
      arrowDownHandler(selectedIndex, workOrderTasks.value.length);
    };

    const onArrowUp = (evt: any) => {
      evt.preventDefault();
      arrowUpHandler(selectedIndex);
    };

    const filterOnInput = ({ target }: { target: HTMLInputElement }) => {
      filterOnInputHandler(
        apiWorkOrderTasks,
        workOrderTasks,

        target.value,
        filter,
        debouncedFilteredWorkOrderTasks,

        sortKey,
        sortAsc,
        updateSort,

        selectedIndex,
      );
    };

    const filterOnEnter = (evt: any) => {
      evt.preventDefault();
      let tmpResult = workOrderTasks.value[selectedIndex.value];

      if (!tmpResult) {
        tmpResult = workOrderTasks.value[0];
      }

      if (props.useMode === "pickerMode") {
        sendWorkOrderTask(tmpResult);
      } else {
        ctx.root.$router.push(showRouteBuilder(tmpResult.work_order_id));
      }
    };

    const sendWorkOrderTask = (il: WorkOrderTask) => {
      ctx.emit("sendWorkOrderTask", il);
    };

    return {
      dataLoaded,
      loadingMessage,
      showRouteBuilder,

      filter,
      filterOnEnter,
      filterOnInput,
      clearFilter,

      workOrderTasks,

      localSortIcon,
      updateSort,

      onArrowDown,
      onArrowUp,
      selectedIndex,
      sendWorkOrderTask,

      firstInput,
    };
  },
});
